import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { portalCommands } from 'src/app/utils/portal-path';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import * as L from 'leaflet';
import * as QRCode from 'qrcode';
import { StudentsService } from 'src/app/services/students.service';
import { ClassesService } from 'src/app/services/classes.service';
import { ParentsService } from 'src/app/services/parents.service';
import { BusesService } from 'src/app/services/buses.service';
import { NationalitiesService } from 'src/app/services/nationalities.service';
import { ReligionsService } from 'src/app/services/religions.service';
import { AcademicYearsService } from 'src/app/services/academic-years.service';
import { LocationsService } from 'src/app/services/locations.service';
import { LanguageService } from 'src/app/services/language.service';
import { PermissionService } from 'src/app/services/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { SearchableSelectOption } from 'src/app/shared/searchable-select/searchable-select.component';

const DEFAULT_MAP_CENTER: L.LatLngExpression = [24.4539, 54.3773];
const DEFAULT_MAP_ZOOM = 7;

// Leaflet default marker icons break under Angular bundling; use CDN assets.
const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

@Component({
  selector: 'app-student-form',
  templateUrl: './student-form.component.html',
  styleUrls: ['./student-form.component.css']
})
export class StudentFormComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapHost') mapHost?: ElementRef<HTMLDivElement>;

  isNew = true;
  studentId = '';
  loading = true;
  saving = false;
  locating = false;
  isError = false;
  errorMessage = '';
  mapReady = false;
  mapScrollZoomActive = false;
  qrCode = '';
  qrDataUrl = '';

  classes: any[] = [];
  parents: any[] = [];
  buses: any[] = [];
  nationalities: any[] = [];
  religions: any[] = [];
  academicYears: any[] = [];
  emirates: any[] = [];
  areas: any[] = [];
  areaSelectOptions: SearchableSelectOption[] = [];
  showCreateParent = false;
  creatingParent = false;
  createParentError = '';
  showCreateLookup: 'nationality' | 'religion' | null = null;
  creatingLookup = false;
  createLookupError = '';

  readonly bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  readonly statuses = ['active', 'inactive', 'graduated', 'transferred', 'withdrawn'];
  readonly relations = ['father', 'mother', 'guardian'];
  readonly contactLabels = ['mobile', 'home', 'work'];

  private map?: L.Map;
  private marker?: L.Marker;
  private mapInitAttempts = 0;
  private emirateSub?: Subscription;
  private areaSub?: Subscription;
  private areasRequestId = 0;

  form = new FormGroup({
    firstNameAr: new FormControl('', [Validators.required]),
    lastNameAr: new FormControl('', [Validators.required]),
    firstNameEn: new FormControl('', [Validators.required]),
    lastNameEn: new FormControl('', [Validators.required]),
    studentCode: new FormControl(''),
    nationalId: new FormControl(''),
    dateOfBirth: new FormControl(''),
    enrollmentDate: new FormControl(''),
    academicYear: new FormControl(''),
    status: new FormControl('active'),
    bloodType: new FormControl(''),
    photo: new FormControl(''),
    sex: new FormControl(''),
    nationality: new FormControl(''),
    religion: new FormControl(''),
    class: new FormControl('', [Validators.required]),
    bus: new FormControl(''),
    address: new FormGroup({
      line1: new FormControl(''),
      line2: new FormControl(''),
      building: new FormControl(''),
      flat: new FormControl(''),
      emirate: new FormControl(''),
      area: new FormControl(''),
      landmark: new FormControl(''),
      coordinates: new FormGroup({
        lat: new FormControl<number | null>(null),
        lng: new FormControl<number | null>(null),
      }),
    }),
    phones: new FormArray([]),
    emails: new FormArray([]),
    parents: new FormArray([]),
  });

  newParentForm = new FormGroup({
    firstNameAr: new FormControl('', [Validators.required]),
    lastNameAr: new FormControl('', [Validators.required]),
    firstNameEn: new FormControl('', [Validators.required]),
    lastNameEn: new FormControl('', [Validators.required]),
    phone: new FormControl(''),
    relation: new FormControl('father', [Validators.required]),
    isPrimaryEmergency: new FormControl(false),
  });

  newLookupForm = new FormGroup({
    nameAr: new FormControl('', [Validators.required]),
    nameEn: new FormControl('', [Validators.required]),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private studentsService: StudentsService,
    private classesService: ClassesService,
    private parentsService: ParentsService,
    private busesService: BusesService,
    private nationalitiesService: NationalitiesService,
    private religionsService: ReligionsService,
    private academicYearsService: AcademicYearsService,
    private locationsService: LocationsService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    public languageService: LanguageService,
    private permissions: PermissionService,
  ) {}

  get canSave(): boolean {
    return this.isNew
      ? this.permissions.canAdd('students')
      : this.permissions.canEdit('students');
  }

  get noneLabel(): string {
    return this.translate.instant('COMMON.NONE');
  }

  get searchPlaceholder(): string {
    return this.translate.instant('COMMON.SEARCH');
  }

  get sexOptions(): SearchableSelectOption[] {
    return [
      { value: 'male', label: this.translate.instant('STUDENTS.SEX_MALE') },
      { value: 'female', label: this.translate.instant('STUDENTS.SEX_FEMALE') },
    ];
  }

  get bloodTypeOptions(): SearchableSelectOption[] {
    return this.bloodTypes.map(type => ({ value: type, label: type }));
  }

  get statusOptions(): SearchableSelectOption[] {
    return this.statuses.map(status => ({
      value: status,
      label: this.translate.instant(`STUDENTS.STATUS_${status.toUpperCase()}`),
    }));
  }

  get classOptions(): SearchableSelectOption[] {
    return this.classes.map(cls => ({
      value: cls.id,
      label: cls.major?.name ? `${cls.name} — ${cls.major.name}` : cls.name,
      searchText: `${cls.name} ${cls.major?.name || ''}`,
    }));
  }

  get academicYearOptions(): SearchableSelectOption[] {
    return this.academicYears.map(year => ({
      value: year.id,
      label: this.bilingualLabel(year),
      searchText: `${year.nameAr || ''} ${year.nameEn || ''} ${year.code || ''}`,
    }));
  }

  get nationalityOptions(): SearchableSelectOption[] {
    return this.nationalities.map(item => ({
      value: item.id,
      label: this.bilingualLabel(item),
      searchText: `${item.nameAr || ''} ${item.nameEn || ''}`,
    }));
  }

  get religionOptions(): SearchableSelectOption[] {
    return this.religions.map(item => ({
      value: item.id,
      label: this.bilingualLabel(item),
      searchText: `${item.nameAr || ''} ${item.nameEn || ''}`,
    }));
  }

  get busOptions(): SearchableSelectOption[] {
    return this.buses.map(bus => ({
      value: bus.id,
      label: `${bus.number} — ${bus.name}`,
      searchText: `${bus.number} ${bus.name} ${bus.plateNumber || ''} ${bus.driverName || ''}`,
    }));
  }

  get emirateOptions(): SearchableSelectOption[] {
    const isAr = this.languageService.current === 'ar';
    return this.emirates.map(emirate => ({
      value: emirate.code,
      label: isAr ? emirate.nameAr : emirate.nameEn,
      searchText: `${emirate.nameAr} ${emirate.nameEn} ${emirate.code}`,
    }));
  }

  get areaOptions(): SearchableSelectOption[] {
    return this.areaSelectOptions;
  }

  get parentOptions(): SearchableSelectOption[] {
    return this.parents.map(parent => ({
      value: parent.id,
      label: this.bilingualLabel(parent),
      searchText: `${parent.firstNameAr || ''} ${parent.lastNameAr || ''} ${parent.firstNameEn || ''} ${parent.lastNameEn || ''}`,
    }));
  }

  get relationOptions(): SearchableSelectOption[] {
    return this.relations.map(relation => ({
      value: relation,
      label: this.translate.instant(`STUDENTS.RELATION_${relation.toUpperCase()}`),
    }));
  }

  get contactLabelOptions(): SearchableSelectOption[] {
    return this.contactLabels.map(label => ({
      value: label,
      label: this.translate.instant(`STUDENTS.LABEL_${label.toUpperCase()}`),
    }));
  }

  get phones(): FormArray {
    return this.form.get('phones') as FormArray;
  }

  get emails(): FormArray {
    return this.form.get('emails') as FormArray;
  }

  get parentLinks(): FormArray {
    return this.form.get('parents') as FormArray;
  }

  get photoValue(): string {
    return (this.form.value.photo || '').trim();
  }

  get previewName(): string {
    const isAr = this.languageService.current === 'ar';
    const first = (isAr ? this.form.value.firstNameAr : this.form.value.firstNameEn) || '';
    const last = (isAr ? this.form.value.lastNameAr : this.form.value.lastNameEn) || '';
    return `${first} ${last}`.trim();
  }

  get previewInitials(): string {
    const name = this.previewName;
    if (!name) {
      return 'ST';
    }
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || 'ST';
  }

  get hasMapPin(): boolean {
    const lat = this.form.get('address.coordinates.lat')?.value;
    const lng = this.form.get('address.coordinates.lng')?.value;
    return lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
  }

  ngAfterViewInit(): void {
    if (!this.loading) {
      this.queueMapInit();
    }
  }

  ngOnDestroy(): void {
    this.emirateSub?.unsubscribe();
    this.areaSub?.unsubscribe();
    this.destroyMap();
  }

  async ngOnInit() {
    this.emirateSub = this.form.get('address.emirate')!.valueChanges.pipe(
      distinctUntilChanged(),
    ).subscribe((emirate) => {
      void this.onEmirateChange(emirate || '', true, true);
    });
    this.areaSub = this.form.get('address.area')!.valueChanges.pipe(
      distinctUntilChanged(),
    ).subscribe((area) => {
      void this.onAreaChange(area || '');
    });

    await this.loadLookups();
    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      if (!id || id === 'new') {
        this.isNew = true;
        this.studentId = '';
        this.loading = false;
        this.queueMapInit();
        return;
      }
      this.isNew = false;
      this.studentId = id;
      await this.loadStudent(id);
    });
  }

  async loadLookups() {
    const [classes, parents, buses, nationalities, religions, academicYears, emirates] = await Promise.all([
      this.classesService.list(),
      this.parentsService.list(),
      this.busesService.list(),
      this.nationalitiesService.list(),
      this.religionsService.list(),
      this.academicYearsService.list(),
      this.locationsService.listEmirates(),
    ]);
    this.classes = classes;
    this.parents = parents;
    this.buses = buses;
    this.nationalities = nationalities;
    this.religions = religions;
    this.academicYears = academicYears;
    this.emirates = emirates;
  }

  async loadStudent(id: string) {
    this.loading = true;
    this.isError = false;
    this.destroyMap();
    try {
      const student = await this.studentsService.getById(id);
      if (!student) {
        this.errorMessage = 'STUDENTS.NOT_FOUND';
        this.isError = true;
        return;
      }
      this.form.patchValue({
        firstNameAr: student.firstNameAr || '',
        lastNameAr: student.lastNameAr || '',
        firstNameEn: student.firstNameEn || '',
        lastNameEn: student.lastNameEn || '',
        studentCode: student.studentCode || '',
        nationalId: student.nationalId || '',
        dateOfBirth: student.dateOfBirth || '',
        enrollmentDate: student.enrollmentDate || '',
        academicYear: student.academicYear?.id || '',
        status: student.status || 'active',
        bloodType: student.bloodType || '',
        photo: student.photo || '',
        sex: student.sex || '',
        nationality: student.nationality?.id || '',
        religion: student.religion?.id || '',
        class: student.class?.id || '',
        bus: student.bus?.id || '',
        address: {
          line1: student.address?.line1 || '',
          line2: student.address?.line2 || '',
          building: student.address?.building || '',
          flat: student.address?.flat || '',
          emirate: student.address?.emirate || '',
          area: '',
          landmark: student.address?.landmark || '',
          coordinates: {
            lat: student.address?.coordinates?.lat ?? null,
            lng: student.address?.coordinates?.lng ?? null,
          },
        },
      }, { emitEvent: false });
      await this.setQrCode(student.qrCode);
      this.phones.clear();
      (student.phones || []).forEach((phone: any) => this.phones.push(this.createContactGroup(phone)));
      this.emails.clear();
      (student.emails || []).forEach((email: any) => this.emails.push(this.createContactGroup(email)));
      this.parentLinks.clear();
      (student.parents || []).forEach((link: any) => this.parentLinks.push(this.createParentLinkGroup(link)));
      if (student.address?.emirate) {
        await this.onEmirateChange(student.address.emirate, false, false);
        this.form.get('address.area')?.setValue(student.address.area || '', { emitEvent: false });
      } else {
        this.areas = [];
      }
    } catch {
      this.isError = true;
      this.errorMessage = 'STUDENTS.LOAD_FAILED';
    } finally {
      this.loading = false;
      this.queueMapInit();
    }
  }

  private async setQrCode(value?: string) {
    this.qrCode = String(value || '').trim();
    if (!this.qrCode) {
      this.qrDataUrl = '';
      return;
    }
    try {
      this.qrDataUrl = await QRCode.toDataURL(this.qrCode, {
        width: 148,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#173654', light: '#ffffff' },
      });
    } catch {
      this.qrDataUrl = '';
    }
  }

  createContactGroup(entry?: { value?: string; label?: string; isPrimary?: boolean }) {
    return new FormGroup({
      value: new FormControl(entry?.value || '', [Validators.required]),
      label: new FormControl(entry?.label || 'mobile'),
      isPrimary: new FormControl(!!entry?.isPrimary),
    });
  }

  createParentLinkGroup(link?: { parent?: any; relation?: string; isPrimaryEmergency?: boolean }) {
    return new FormGroup({
      parent: new FormControl(link?.parent?.id || link?.parent || '', [Validators.required]),
      relation: new FormControl(link?.relation || 'father', [Validators.required]),
      isPrimaryEmergency: new FormControl(!!link?.isPrimaryEmergency),
    });
  }

  addPhone() {
    this.phones.push(this.createContactGroup());
  }

  removePhone(index: number) {
    this.phones.removeAt(index);
  }

  addEmail() {
    this.emails.push(this.createContactGroup());
  }

  removeEmail(index: number) {
    this.emails.removeAt(index);
  }

  addParentLink() {
    this.parentLinks.push(this.createParentLinkGroup());
  }

  removeParentLink(index: number) {
    this.parentLinks.removeAt(index);
  }

  openCreateParent() {
    this.createParentError = '';
    this.newParentForm.reset({
      firstNameAr: '',
      lastNameAr: '',
      firstNameEn: '',
      lastNameEn: '',
      phone: '',
      relation: 'father',
      isPrimaryEmergency: this.parentLinks.length === 0,
    });
    this.showCreateParent = true;
  }

  closeCreateParent() {
    this.showCreateParent = false;
    this.createParentError = '';
    this.creatingParent = false;
  }

  async createParentAndLink() {
    if (this.newParentForm.invalid) {
      this.newParentForm.markAllAsTouched();
      this.createParentError = 'COMMON.REQUIRED_FIELDS';
      return;
    }

    const raw = this.newParentForm.getRawValue();
    const phone = (raw.phone || '').trim();
    this.creatingParent = true;
    this.createParentError = '';
    try {
      const created = await this.parentsService.create({
        firstNameAr: raw.firstNameAr,
        lastNameAr: raw.lastNameAr,
        firstNameEn: raw.firstNameEn,
        lastNameEn: raw.lastNameEn,
        phones: phone ? [{ value: phone, label: 'mobile', isPrimary: true }] : [],
        emails: [],
      });
      this.parents = [created, ...this.parents.filter(parent => parent.id !== created.id)];
      this.parentLinks.push(this.createParentLinkGroup({
        parent: created.id,
        relation: raw.relation || 'father',
        isPrimaryEmergency: !!raw.isPrimaryEmergency,
      }));
      this.closeCreateParent();
    } catch (error: any) {
      this.createParentError = error?.error?.errorMessages?.[0]?.message || 'STUDENTS.CREATE_PARENT_FAILED';
    } finally {
      this.creatingParent = false;
    }
  }

  openCreateLookup(kind: 'nationality' | 'religion') {
    this.createLookupError = '';
    this.newLookupForm.reset({ nameAr: '', nameEn: '' });
    this.showCreateLookup = kind;
  }

  closeCreateLookup() {
    this.showCreateLookup = null;
    this.createLookupError = '';
    this.creatingLookup = false;
  }

  get createLookupTitleKey(): string {
    return this.showCreateLookup === 'religion'
      ? 'STUDENTS.CREATE_RELIGION_TITLE'
      : 'STUDENTS.CREATE_NATIONALITY_TITLE';
  }

  get createLookupSaveKey(): string {
    return this.showCreateLookup === 'religion'
      ? 'STUDENTS.CREATE_RELIGION'
      : 'STUDENTS.CREATE_NATIONALITY';
  }

  async createLookupAndAssign() {
    if (!this.showCreateLookup) {
      return;
    }
    if (this.newLookupForm.invalid) {
      this.newLookupForm.markAllAsTouched();
      this.createLookupError = 'COMMON.REQUIRED_FIELDS';
      return;
    }

    const raw = this.newLookupForm.getRawValue();
    const kind = this.showCreateLookup;
    this.creatingLookup = true;
    this.createLookupError = '';
    try {
      if (kind === 'nationality') {
        const created = await this.nationalitiesService.create({
          nameAr: raw.nameAr,
          nameEn: raw.nameEn,
        });
        this.nationalities = [created, ...this.nationalities.filter(item => item.id !== created.id)];
        this.form.get('nationality')?.setValue(created.id);
      } else {
        const created = await this.religionsService.create({
          nameAr: raw.nameAr,
          nameEn: raw.nameEn,
        });
        this.religions = [created, ...this.religions.filter(item => item.id !== created.id)];
        this.form.get('religion')?.setValue(created.id);
      }
      this.closeCreateLookup();
    } catch (error: any) {
      this.createLookupError = error?.error?.errorMessages?.[0]?.message
        || (kind === 'religion' ? 'STUDENTS.CREATE_RELIGION_FAILED' : 'STUDENTS.CREATE_NATIONALITY_FAILED');
    } finally {
      this.creatingLookup = false;
    }
  }

  async onEmirateChange(emirate: string, clearArea = true, recenter = true) {
    const requestId = ++this.areasRequestId;
    if (clearArea) {
      this.form.get('address.area')?.setValue('', { emitEvent: false });
    }

    if (!emirate) {
      this.areas = [];
      this.areaSelectOptions = [];
      this.cdr.detectChanges();
      return;
    }

    try {
      const areas = await this.locationsService.listAreas(emirate);
      if (requestId !== this.areasRequestId) {
        return;
      }
      this.areas = Array.isArray(areas) ? areas : [];
      this.areaSelectOptions = this.buildAreaOptions(this.areas);
      this.cdr.detectChanges();
    } catch (error: any) {
      if (requestId !== this.areasRequestId) {
        return;
      }
      this.areas = [];
      this.areaSelectOptions = [];
      this.isError = true;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'STUDENTS.AREAS_LOAD_FAILED';
      this.cdr.detectChanges();
      return;
    }

    if (recenter && !this.hasMapPin) {
      await this.panToLocation(emirate || undefined, undefined, false);
    }
  }

  private buildAreaOptions(areas: any[]): SearchableSelectOption[] {
    const isAr = this.languageService.current === 'ar';
    return areas.map(area => ({
      value: area.code,
      label: isAr ? area.nameAr : area.nameEn,
      searchText: `${area.nameAr || ''} ${area.nameEn || ''} ${area.code || ''}`,
    }));
  }

  async onAreaChange(area: string) {
    const emirate = this.form.get('address.emirate')?.value || '';
    if (!this.hasMapPin) {
      await this.panToLocation(emirate || undefined, area || undefined, false);
    }
  }

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.PHOTO_INVALID';
      input.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.PHOTO_TOO_LARGE';
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.form.get('photo')?.setValue(String(reader.result || ''));
      this.isError = false;
    };
    reader.readAsDataURL(file);
  }

  clearPhoto() {
    this.form.get('photo')?.setValue('');
  }

  clearMapPin() {
    this.form.get('address.coordinates')?.patchValue({ lat: null, lng: null });
    this.clearMarker();
  }

  async useCurrentLocation() {
    if (!navigator.geolocation) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.GEOLOCATION_UNSUPPORTED';
      return;
    }
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.GEOLOCATION_INSECURE';
      return;
    }

    this.locating = true;
    this.isError = false;
    this.errorMessage = '';

    try {
      const position = await this.readCurrentPosition();
      this.zone.run(() => {
        this.setCoordinates(position.coords.latitude, position.coords.longitude, true);
        this.locating = false;
        this.isError = false;
        this.cdr.markForCheck();
      });
    } catch (error) {
      this.zone.run(() => {
        this.locating = false;
        this.isError = true;
        this.errorMessage = this.geolocationErrorKey(error);
        this.cdr.markForCheck();
      });
    }
  }

  private readCurrentPosition(): Promise<GeolocationPosition> {
    const tryOnce = (options: PositionOptions) =>
      new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });

    return tryOnce({
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 60000,
    }).catch(() =>
      tryOnce({
        enableHighAccuracy: false,
        timeout: 20000,
        maximumAge: 300000,
      })
    );
  }

  private geolocationErrorKey(error: unknown): string {
    const code = (error as GeolocationPositionError)?.code;
    if (code === 1) {
      return 'STUDENTS.GEOLOCATION_DENIED';
    }
    if (code === 2) {
      return 'STUDENTS.GEOLOCATION_UNAVAILABLE';
    }
    if (code === 3) {
      return 'STUDENTS.GEOLOCATION_TIMEOUT';
    }
    return 'STUDENTS.GEOLOCATION_FAILED';
  }

  bilingualLabel(item: {
    nameAr?: string;
    nameEn?: string;
    firstNameAr?: string;
    lastNameAr?: string;
    firstNameEn?: string;
    lastNameEn?: string;
  }): string {
    const isAr = this.languageService.current === 'ar';
    if (item.nameAr || item.nameEn) {
      return isAr ? (item.nameAr || item.nameEn || '') : (item.nameEn || item.nameAr || '');
    }
    return isAr
      ? `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim()
      : `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim();
  }

  get studentsLink(): string[] {
    return portalCommands(this.router, 'settings', 'students');
  }

  get profileLink(): string[] {
    return portalCommands(this.router, 'settings', 'students', this.studentId);
  }

  goBack() {
    if (!this.isNew && this.studentId) {
      this.router.navigate(portalCommands(this.router, 'settings', 'students', this.studentId));
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'students'));
  }

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.isError = true;
      this.errorMessage = 'COMMON.REQUIRED_FIELDS';
      return;
    }

    const raw = this.form.getRawValue();
    const lat = raw.address.coordinates.lat;
    const lng = raw.address.coordinates.lng;
    const hasCoords = lat != null && lng != null && lat !== ('' as any) && lng !== ('' as any);

    const body: Record<string, unknown> = {
      firstNameAr: raw.firstNameAr,
      lastNameAr: raw.lastNameAr,
      firstNameEn: raw.firstNameEn,
      lastNameEn: raw.lastNameEn,
      studentCode: raw.studentCode || undefined,
      nationalId: raw.nationalId || undefined,
      dateOfBirth: raw.dateOfBirth || undefined,
      enrollmentDate: raw.enrollmentDate || undefined,
      academicYear: raw.academicYear || null,
      status: raw.status || 'active',
      bloodType: raw.bloodType || null,
      photo: raw.photo || null,
      sex: raw.sex || null,
      nationality: raw.nationality || null,
      religion: raw.religion || null,
      class: raw.class,
      bus: raw.bus || null,
      address: {
        line1: raw.address.line1 || undefined,
        line2: raw.address.line2 || undefined,
        building: raw.address.building || undefined,
        flat: raw.address.flat || undefined,
        emirate: raw.address.emirate || undefined,
        area: raw.address.area || undefined,
        landmark: raw.address.landmark || undefined,
        coordinates: hasCoords
          ? { lat: Number(lat), lng: Number(lng) }
          : undefined,
      },
      phones: (raw.phones || []).filter((p: any) => p.value),
      emails: (raw.emails || []).filter((e: any) => e.value),
      parents: (raw.parents || []).map((link: any) => ({
        parent: link.parent,
        relation: link.relation,
        isPrimaryEmergency: !!link.isPrimaryEmergency,
      })),
    };

    this.saving = true;
    this.isError = false;
    try {
      if (this.isNew) {
        const created = await this.studentsService.create(body);
        this.router.navigate(portalCommands(this.router, 'settings', 'students', created.id));
      } else {
        await this.studentsService.update(this.studentId, body);
        this.router.navigate(portalCommands(this.router, 'settings', 'students', this.studentId));
      }
    } catch (error: any) {
      this.isError = true;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'STUDENTS.SAVE_FAILED';
    } finally {
      this.saving = false;
    }
  }

  private queueMapInit() {
    setTimeout(() => this.initMap(), 0);
  }

  private initMap() {
    if (this.loading || this.map || !this.mapHost?.nativeElement) {
      if (!this.loading && !this.map && this.mapInitAttempts < 10) {
        this.mapInitAttempts += 1;
        setTimeout(() => this.initMap(), 50);
      }
      return;
    }

    this.mapInitAttempts = 0;
    this.map = L.map(this.mapHost.nativeElement, {
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM,
      // Prevent page-scroll from accidentally zooming the map.
      scrollWheelZoom: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);

    this.map.on('focus', () => this.enableMapScrollZoom());
    this.map.on('click', (event: L.LeafletMouseEvent) => {
      this.enableMapScrollZoom();
      this.setCoordinates(event.latlng.lat, event.latlng.lng, false);
    });
    this.map.on('mouseout', () => this.disableMapScrollZoom());
    this.map.getContainer().addEventListener('blur', () => this.disableMapScrollZoom());

    if (this.hasMapPin) {
      const lat = Number(this.form.get('address.coordinates.lat')?.value);
      const lng = Number(this.form.get('address.coordinates.lng')?.value);
      this.setMarker(lat, lng, true);
    } else {
      const emirate = this.form.get('address.emirate')?.value || '';
      const area = this.form.get('address.area')?.value || '';
      void this.panToLocation(emirate || undefined, area || undefined, false);
    }

    setTimeout(() => {
      this.map?.invalidateSize();
      this.mapReady = true;
      this.cdr.markForCheck();
    }, 100);
  }

  private enableMapScrollZoom(): void {
    if (!this.map || this.mapScrollZoomActive) {
      return;
    }
    this.map.scrollWheelZoom.enable();
    this.mapScrollZoomActive = true;
    this.cdr.markForCheck();
  }

  private disableMapScrollZoom(): void {
    if (!this.map || !this.mapScrollZoomActive) {
      return;
    }
    this.map.scrollWheelZoom.disable();
    this.mapScrollZoomActive = false;
    this.cdr.markForCheck();
  }

  private destroyMap() {
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
    this.marker = undefined;
    this.mapReady = false;
    this.mapScrollZoomActive = false;
  }

  private async panToLocation(emirate?: string, area?: string, placeMarker = false) {
    try {
      const center = await this.locationsService.getCenter(emirate, area);
      const zoom = center.zoom || (emirate ? 11 : DEFAULT_MAP_ZOOM);
      this.map?.setView([center.lat, center.lng], zoom);
      if (placeMarker) {
        this.setCoordinates(center.lat, center.lng, false);
      }
    } catch {
      this.map?.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
    }
  }

  private setCoordinates(lat: number, lng: number, fly = true) {
    const roundedLat = Number(lat.toFixed(6));
    const roundedLng = Number(lng.toFixed(6));
    this.form.get('address.coordinates')?.patchValue({
      lat: roundedLat,
      lng: roundedLng,
    });
    if (!this.map) {
      this.queueMapInit();
      setTimeout(() => this.setMarker(roundedLat, roundedLng, fly), 80);
      return;
    }
    this.setMarker(roundedLat, roundedLng, fly);
    this.map.invalidateSize();
  }

  private setMarker(lat: number, lng: number, fly = true) {
    if (!this.map) {
      return;
    }
    const latLng: L.LatLngExpression = [lat, lng];
    if (!this.marker) {
      this.marker = L.marker(latLng, { draggable: true, icon: markerIcon }).addTo(this.map);
      this.marker.on('dragend', () => {
        const position = this.marker?.getLatLng();
        if (position) {
          this.setCoordinates(position.lat, position.lng, false);
        }
      });
    } else {
      this.marker.setLatLng(latLng);
    }
    if (fly) {
      this.map.setView(latLng, Math.max(this.map.getZoom(), 14));
    }
  }

  private clearMarker() {
    if (this.marker && this.map) {
      this.map.removeLayer(this.marker);
    }
    this.marker = undefined;
  }
}
